// ─────────────────────────────────────────────────────────────────────────────
// LCCA NEXUS — LCCA Input Panel
// Full parameter form with live validation via react-hook-form + zod
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { nanoid } from 'nanoid';
import { Plus, Trash2, Info, Zap, Wrench, RotateCcw, DollarSign } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Input, SectionLabel, Tooltip, Card, CardHeader, CardBody } from '@/components/ui/index';
import { cn } from '@/lib/utils';
import type { LCCAParameters } from '@lcca-nexus/shared';

// ── Zod schema ────────────────────────────────────────────────────────────────
const schema = z.object({
  analysisPeriod:   z.coerce.number().int().min(1).max(100),
  discountRate:     z.coerce.number().min(0.01).max(50),
  initialCost:      z.coerce.number().min(0),
  residualValue:    z.coerce.number().min(0),
  annualOM:         z.coerce.number().min(0),
  omEscalation:     z.coerce.number().min(0).max(50),
  annualEnergy:     z.coerce.number().min(0),
  energyEscalation: z.coerce.number().min(0).max(50),
  replacements: z.array(z.object({
    id:    z.string(),
    label: z.string().min(1, 'Description required'),
    cost:  z.coerce.number().min(0),
    year:  z.coerce.number().int().min(1),
  })),
});

type FormValues = z.infer<typeof schema>;

interface InputPanelProps {
  defaultValues?: Partial<LCCAParameters>;
  onCalculate: (params: LCCAParameters) => void;
  onSave?: (params: LCCAParameters) => void;
  isCalculating?: boolean;
  isSaving?: boolean;
}

export function InputPanel({
  defaultValues, onCalculate, onSave, isCalculating, isSaving,
}: InputPanelProps) {
  const {
    register, control, handleSubmit, watch, reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      analysisPeriod:   25,
      discountRate:     5,
      initialCost:      150000,
      residualValue:    15000,
      annualOM:         8000,
      omEscalation:     2,
      annualEnergy:     12000,
      energyEscalation: 3,
      replacements: [
        { id: nanoid(), label: 'Compressor Replacement', cost: 25000, year: 10 },
        { id: nanoid(), label: 'Full System Overhaul',   cost: 40000, year: 20 },
      ],
      ...defaultValues,
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'replacements' });
  const analysisPeriod = watch('analysisPeriod');

  // Reset when defaultValues changes (e.g., loading a saved scenario)
  useEffect(() => {
    if (defaultValues) reset({ ...defaultValues } as FormValues);
  }, [JSON.stringify(defaultValues)]); // eslint-disable-line

  const onSubmit = (values: FormValues) => {
    onCalculate(values as LCCAParameters);
  };

  const handleSave = handleSubmit((values) => {
    onSave?.(values as LCCAParameters);
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="flex flex-col gap-4">

        {/* ── Study Period ── */}
        <Card>
          <CardHeader>
            <div className="w-8 h-8 rounded-lg bg-navy-50 flex items-center justify-center text-navy-600">
              <Info size={16} />
            </div>
            <div>
              <div className="font-display font-bold text-sm text-navy-900">Study Parameters</div>
              <div className="text-xs text-navy-400">Analysis period and discount rate</div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Analysis Period"
                type="number"
                suffix="years"
                error={errors.analysisPeriod?.message}
                hint="1 – 100 years"
                {...register('analysisPeriod')}
              />
              <Input
                label="Discount Rate"
                type="number"
                suffix="% / yr"
                step="0.1"
                error={errors.discountRate?.message}
                hint="Typical: 3–8%"
                {...register('discountRate')}
              />
            </div>
          </CardBody>
        </Card>

        {/* ── Initial Cost ── */}
        <Card>
          <CardHeader>
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600">
              <DollarSign size={16} />
            </div>
            <div>
              <div className="font-display font-bold text-sm text-navy-900">Capital Cost</div>
              <div className="text-xs text-navy-400">Year 0 investment & end-of-life value</div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Initial Capital Cost"
                type="number"
                prefix="$"
                error={errors.initialCost?.message}
                hint="Total at Year 0"
                {...register('initialCost')}
              />
              <div>
                <Input
                  label="Residual Value"
                  type="number"
                  prefix="$"
                  error={errors.residualValue?.message}
                  hint={`Salvage at Year ${analysisPeriod || 'N'}`}
                  {...register('residualValue')}
                />
              </div>
            </div>
          </CardBody>
        </Card>

        {/* ── O&M Costs ── */}
        <Card>
          <CardHeader>
            <div className="w-8 h-8 rounded-lg bg-navy-50 flex items-center justify-center text-navy-600">
              <Wrench size={16} />
            </div>
            <div>
              <div className="font-display font-bold text-sm text-navy-900">Operation & Maintenance</div>
              <div className="text-xs text-navy-400">Annual recurring costs</div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Annual O&M Cost"
                type="number"
                prefix="$"
                error={errors.annualOM?.message}
                hint="Base year cost"
                {...register('annualOM')}
              />
              <Input
                label="O&M Escalation"
                type="number"
                suffix="% / yr"
                step="0.1"
                error={errors.omEscalation?.message}
                hint="Typical: 1–4%"
                {...register('omEscalation')}
              />
            </div>
          </CardBody>
        </Card>

        {/* ── Energy Costs ── */}
        <Card>
          <CardHeader>
            <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600">
              <Zap size={16} />
            </div>
            <div>
              <div className="font-display font-bold text-sm text-navy-900">Energy Costs</div>
              <div className="text-xs text-navy-400">Electricity, gas, fuel</div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Annual Energy Cost"
                type="number"
                prefix="$"
                error={errors.annualEnergy?.message}
                hint="Base year cost"
                {...register('annualEnergy')}
              />
              <Input
                label="Energy Escalation"
                type="number"
                suffix="% / yr"
                step="0.1"
                error={errors.energyEscalation?.message}
                hint="Typical: 2–6%"
                {...register('energyEscalation')}
              />
            </div>
          </CardBody>
        </Card>

        {/* ── Replacements ── */}
        <Card>
          <CardHeader>
            <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center text-red-600">
              <RotateCcw size={16} />
            </div>
            <div>
              <div className="font-display font-bold text-sm text-navy-900">Replacements</div>
              <div className="text-xs text-navy-400">Major capital expenditures during study</div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flex flex-col gap-2 mb-3">
              {/* Column headers */}
              {fields.length > 0 && (
                <div className="grid grid-cols-[1fr_100px_70px_32px] gap-2 px-1">
                  <span className="text-[10px] font-semibold text-navy-400 uppercase tracking-wide">Description</span>
                  <span className="text-[10px] font-semibold text-navy-400 uppercase tracking-wide">Cost ($)</span>
                  <span className="text-[10px] font-semibold text-navy-400 uppercase tracking-wide">Year</span>
                  <span />
                </div>
              )}

              {fields.map((field, i) => (
                <div key={field.id} className="grid grid-cols-[1fr_100px_70px_32px] gap-2 items-start">
                  <input
                    className={cn('input-base text-sm', errors.replacements?.[i]?.label && 'error')}
                    placeholder="e.g. Compressor"
                    {...register(`replacements.${i}.label`)}
                  />
                  <input
                    type="number"
                    className={cn('input-base text-sm', errors.replacements?.[i]?.cost && 'error')}
                    placeholder="25000"
                    {...register(`replacements.${i}.cost`)}
                  />
                  <input
                    type="number"
                    className={cn('input-base text-sm', errors.replacements?.[i]?.year && 'error')}
                    placeholder="10"
                    min="1"
                    max={analysisPeriod}
                    {...register(`replacements.${i}.year`)}
                  />
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    className="w-8 h-10 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => append({ id: nanoid(), label: '', cost: 0, year: Math.ceil((analysisPeriod || 25) / 2) })}
              className="w-full h-9 border border-dashed border-parchment-300 hover:border-navy-400 text-navy-400 hover:text-navy-600 text-sm rounded-lg flex items-center justify-center gap-2 transition-colors bg-transparent"
            >
              <Plus size={14} />
              Add Replacement Event
            </button>
          </CardBody>
        </Card>

        {/* ── Actions ── */}
        <div className="flex flex-col gap-2.5">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={isCalculating}
            className="w-full"
            icon={<span className="text-base">🔢</span>}
          >
            Calculate LCCA
          </Button>

          {onSave && (
            <Button
              type="button"
              variant="gold"
              size="md"
              loading={isSaving}
              className="w-full"
              onClick={handleSave}
            >
              Save Scenario
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full text-navy-400"
            onClick={() => reset()}
          >
            Reset Inputs
          </Button>
        </div>

      </div>
    </form>
  );
}
